import { useEffect, useRef, useState } from 'react';

export interface DateRange {
  from: string;
  to: string;
}

interface DateRangePickerProps {
  value: DateRange;
  onChange: (value: DateRange) => void;
  className?: string;
}

export default function DateRangePicker({ value, onChange, className = '' }: DateRangePickerProps) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<DateRange>(value);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const hasValue = Boolean(value.from || value.to);
  const label = hasValue ? `${value.from || '—'} ← ${value.to || '—'}` : 'نطاق التاريخ';

  return (
    <div ref={ref} className={['relative', className].join(' ')}>
      <button
        type="button"
        onClick={() => {
          setDraft(value);
          setOpen((prev) => !prev);
        }}
        className={[
          'flex h-11 items-center gap-2 rounded-field border bg-white px-3 text-sm transition-colors',
          hasValue ? 'border-secondary-400 text-foreground-900' : 'border-background-300 text-foreground-500',
          open ? 'ring-2 ring-secondary-100' : '',
        ].join(' ')}
      >
        <i className="ri-calendar-line text-base text-foreground-400" aria-hidden="true"></i>
        <span className="whitespace-nowrap" dir="ltr">
          {label}
        </span>
      </button>

      {open && (
        <div className="dc-fade-up absolute right-0 z-30 mt-1 w-[300px] rounded-card border border-background-200 bg-white p-4 shadow-soft">
          <div className="space-y-3">
            <div>
              <label className="mb-1.5 block text-[12px] font-medium text-foreground-700">من</label>
              <input
                type="date"
                value={draft.from}
                onChange={(event) => setDraft((prev) => ({ ...prev, from: event.target.value }))}
                className="h-10 w-full rounded-field border border-background-300 px-3 text-[13px] text-foreground-900 focus:border-secondary-400 focus:outline-none focus:ring-2 focus:ring-secondary-100"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-[12px] font-medium text-foreground-700">إلى</label>
              <input
                type="date"
                value={draft.to}
                onChange={(event) => setDraft((prev) => ({ ...prev, to: event.target.value }))}
                className="h-10 w-full rounded-field border border-background-300 px-3 text-[13px] text-foreground-900 focus:border-secondary-400 focus:outline-none focus:ring-2 focus:ring-secondary-100"
              />
            </div>
          </div>
          <div className="mt-4 flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                setDraft({ from: '', to: '' });
                onChange({ from: '', to: '' });
                setOpen(false);
              }}
              className="h-9 flex-1 rounded-btn border border-background-300 text-[13px] text-foreground-600 transition-colors hover:bg-background-50"
            >
              مسح
            </button>
            <button
              type="button"
              onClick={() => {
                onChange(draft);
                setOpen(false);
              }}
              className="h-9 flex-1 rounded-btn bg-secondary-500 text-[13px] font-medium text-white transition-colors hover:bg-secondary-600"
            >
              تطبيق
            </button>
          </div>
        </div>
      )}
    </div>
  );
}